import { useState } from "react";
import { Controller, FormProvider, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import InputField from "@components/common/InputField";
import Button from "@components/common/Button/Button";
import PasswordStrengthHints from "@components/common/AuthPasswordFields/PasswordStrengthHints";
import {
	authFieldLabelClassName,
	authFormCompactClassName,
	authFormStackLooseClassName,
	authRootErrorClassName,
} from "@constants/auth.constants";
import useChangePasswordMutate from "../services/queries";

interface ChangePasswordFormProps {
	onSuccess?: () => void;
}

const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({ onSuccess }) => {
	const { t } = useTranslation();
	const { methods, onSubmit, changePasswordMutation } = useChangePasswordMutate(onSuccess);
	const [showOldPassword, setShowOldPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	const {
		handleSubmit,
		control,
		setValue,
		clearErrors,
		formState: { errors },
	} = methods;

	const newPasswordValue = useWatch({ control, name: "newPassword" }) || "";

	const renderVisibilityToggle = (visible: boolean, toggle: () => void) => (
		<IconButton
			onClick={toggle}
			disabled={changePasswordMutation.isPending}
			size="small"
			type="button"
			aria-label={visible ? "Hide password" : "Show password"}>
			{visible ? <VisibilityOffOutlinedIcon fontSize="medium" /> : <VisibilityOutlinedIcon fontSize="medium" />}
		</IconButton>
	);

	return (
		<>
			<p className="mb-4 text-sm text-gray-600">{t("psdaCommon.profile.changePassword.subtitle")}</p>

			{errors.root ? (
				<div className={`${authRootErrorClassName} mb-4`}>
					<p className="text-sm text-red-600">{errors.root.message}</p>
				</div>
			) : null}

			<FormProvider {...methods}>
				<form
					onSubmit={handleSubmit(onSubmit)}
					className={`${authFormStackLooseClassName} ${authFormCompactClassName}`}
					noValidate>
					<Controller
						name="oldPassword"
						control={control}
						render={({ field, fieldState }) => (
							<InputField
								id="change-password-old"
								label={t("psdaCommon.profile.changePassword.oldPasswordLabel")}
								labelClassName={authFieldLabelClassName}
								placeholder={t("psdaCommon.auth.common.placeholder.passwordPlaceholder")}
								type={showOldPassword ? "text" : "password"}
								required
								startIcon={<LockOutlinedIcon fontSize="medium" />}
								error={!!fieldState.error}
								helperText={fieldState.error?.message}
								disabled={changePasswordMutation.isPending}
								endIcon={renderVisibilityToggle(showOldPassword, () => setShowOldPassword(v => !v))}
								{...field}
								onChange={e => {
									field.onChange(e);
									if (errors.root) clearErrors("root");
								}}
							/>
						)}
					/>

					<Controller
						name="newPassword"
						control={control}
						render={({ field, fieldState }) => (
							<InputField
								id="change-password-new"
								label={t("psdaCommon.profile.changePassword.newPasswordLabel")}
								labelClassName={authFieldLabelClassName}
								placeholder={t("psdaCommon.auth.common.placeholder.passwordPlaceholder")}
								type={showNewPassword ? "text" : "password"}
								required
								startIcon={<LockOutlinedIcon fontSize="medium" />}
								error={!!fieldState.error}
								helperText={fieldState.error?.message}
								disabled={changePasswordMutation.isPending}
								endIcon={renderVisibilityToggle(showNewPassword, () => setShowNewPassword(v => !v))}
								{...field}
								onChange={e => {
									field.onChange(e);
									if (errors.root) clearErrors("root");
								}}
							/>
						)}
					/>

					<PasswordStrengthHints
						password={newPasswordValue}
						onSuggestPassword={suggested => {
							setValue("newPassword", suggested, { shouldValidate: true, shouldDirty: true });
							setValue("confirmPassword", suggested, { shouldValidate: true, shouldDirty: true });
							if (errors.root) clearErrors("root");
						}}
					/>

					<Controller
						name="confirmPassword"
						control={control}
						render={({ field, fieldState }) => (
							<InputField
								id="change-password-confirm"
								label={t("psdaCommon.profile.changePassword.confirmPasswordLabel")}
								labelClassName={authFieldLabelClassName}
								placeholder={t("psdaCommon.auth.common.placeholder.confirmPasswordPlaceholder")}
								type={showConfirmPassword ? "text" : "password"}
								required
								startIcon={<LockOutlinedIcon fontSize="medium" />}
								error={!!fieldState.error}
								helperText={fieldState.error?.message}
								disabled={changePasswordMutation.isPending}
								endIcon={renderVisibilityToggle(showConfirmPassword, () => setShowConfirmPassword(v => !v))}
								{...field}
								onChange={e => {
									field.onChange(e);
									if (errors.root) clearErrors("root");
								}}
							/>
						)}
					/>

					<Button
						type="submit"
						loading={changePasswordMutation.isPending}
						text={t("psdaCommon.profile.changePassword.submit")}
						className="w-full"
						disabled={changePasswordMutation.isPending}
					/>
				</form>
			</FormProvider>
		</>
	);
};

export default ChangePasswordForm;
